<template>
  <v-form class="pa-0">
    <v-card
      v-if="selectedReportEntry"
      class="elevation-0"
    >
      <v-card-title
        class="subtitle-2 font-weight-bold py-2 px-3"
      >
        <v-row>
          <v-col
            v-if="!isEditingReportLabel"
            @click="selectedReportEntry.isEditable && editReportLabelForm()"
          >
            <span>{{ selectedReportEntry.reportLabel }}</span>
          </v-col>

          <v-col v-else>
            <v-form
              ref="reportLabelForm"
              @submit.prevent="saveReportLabelForm"
            >
              <v-text-field
                ref="reportLabelInput"
                v-model="selectedReportEntry.reportLabel"
                :disabled="!selectedReportEntry.isEditable"
                hide-details
                outlined
                single-line
                dense
                @blur="saveReportLabelForm"
              />
            </v-form>
          </v-col>

          <v-col class="col-auto my-auto py-0">
            <v-btn
              :color="secondaryColor"
              outlined
              small
              :disabled="!allowEdits || selectedReportEntry.isTemporary"
              :title="`Copy ${selectedReportEntry.reportLabel}`"
              @click.stop="copyReportToSelectedOrg(selectedReportEntry)"
            >
              <v-icon small>
                content_copy
              </v-icon>

              <span class="pl-1">
                Copy
              </span>
            </v-btn>

            <v-btn
              class="ml-4"
              color="toasted"
              outlined
              small
              :disabled="!selectedReportEntry.canDeleteSoft"
              :title="`Delete ${selectedReportEntry.reportLabel}`"
              @click.stop="confirmDeleteReport"
            >
              <v-icon
                small
                class="material-icons-outlined"
              >
                backspace
              </v-icon>

              <span class="pl-2">
                {{ selectedReportEntry.isTemporary ? 'Cancel' : 'Delete' }}
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-col>
          <v-row
            class="justify-space-between"
          >
            <v-col
              class="col-grow py-2 d-flex align-center"
            >
              <span class="font-weight-bold caption">{{ selectedReportEntry.docIdOnParent ? 'Parent ID' : 'ID' }}</span>
              <span class="pl-1 font-weight-medium caption">{{ selectedReportEntry.docIdOnParent || selectedReportEntry.docId }}</span>
            </v-col>
          </v-row>

          <v-row>
            <v-divider />
          </v-row>

          <v-row class="mt-0">
            <v-col class="px-0">
              <v-card class="elevation-0">
                <v-card-title class="pa-0">
                  <v-tabs
                    v-model="currentSection"
                    :background-color="highlightColor"
                  >
                    <v-tab
                      v-for="section in sections"
                      :key="section.value"
                      :href="`#${section.value}`"
                      :value="section.value"
                    >
                      <span>{{ section.text }}</span>
                    </v-tab>
                  </v-tabs>
                </v-card-title>

                <v-card-text class="pb-0">
                  <v-tabs-items :value="currentSection">
                    <v-tab-item value="parameters">
                      <v-form
                        :ref="`${formName}ForParameters`"
                        v-model="isValidFormForParameters"
                        :disabled="!selectedReportEntry.isEditable"
                        class="col pa-0"
                      >
                        <v-card
                          class="mt-0 elevation-0"
                        >
                          <v-col>
                            <draggable v-model="selectedReportEntry.parameters">
                              <v-row
                                v-for="(param, paramIndex) in selectedReportEntryParameters"
                                :key="paramIndex"
                                :class="paramIndex === 0 ? 'mt-0' : paramIndex !== 0 && (!isDraggingParameter || paramIndex !== draggingParameterIndex) && selectedReportEntryParameters[paramIndex - 1].action === selectedReportEntryParameters[paramIndex].action ? 'mt-0' : 'mt-10'"
                                @dragstart="dragParameter(paramIndex)"
                                @dragend="dropParameter"
                              >
                                <v-col class="pa-0">
                                  <v-row
                                    v-if="paramIndex !== 0 && (!isDraggingParameter || paramIndex !== draggingParameterIndex) && selectedReportEntryParameters[paramIndex - 1].action === selectedReportEntryParameters[paramIndex].action"
                                    class="align-center justify-center pt-3 my-0"
                                  >
                                    <v-col>
                                      <v-row
                                        class="mt-0 justify-center"
                                      >
                                        <v-divider
                                          class="mx-2 py-2"
                                          vertical
                                        />
                                      </v-row>
                                      <v-row
                                        v-if="param.action === 'filterBy'"
                                        class="justify-center"
                                      >
                                        <v-btn
                                          class="rounded-pill"
                                          color="secondary"
                                          outlined
                                          x-small
                                          @click.stop="updateReportParameterConjunction(paramIndex, selectedReportEntry)"
                                        >
                                          {{ param.conjunction || 'THEN' }}
                                        </v-btn>
                                      </v-row>
                                      <v-row
                                        v-if="param.action === 'filterBy'"
                                        class="justify-center"
                                      >
                                        <v-divider
                                          class="mx-2 py-2"
                                          vertical
                                        />
                                      </v-row>
                                    </v-col>
                                  </v-row>

                                  <v-row class="mt-0">
                                    <v-col class="py-0">
                                      <v-card
                                        class="elevation-0"
                                        outlined
                                      >
                                        <v-card-text class="pt-3">
                                          <v-row class="align-center">
                                            <v-col class="col-auto py-0 pl-0 pr-0">
                                              <v-icon
                                                class="mr-2 cursor-grab"
                                                color="coolgray"
                                                small
                                                title="Drag to Reorder Parameter"
                                                :disabled="selectedReportEntryParameters.length <= 1"
                                              >
                                                drag_indicator
                                              </v-icon>
                                            </v-col>

                                            <v-col
                                              :class="`mt-2 py-0 pl-0 ${!param.conditions || !param.conditions.length ? 'col-2' : ''}`"
                                            >
                                              <v-autocomplete
                                                v-model="param.action"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                dense
                                                :items="actionOptions"
                                                label="Action"
                                                name="actions"
                                                :rules="rulesRequired(selectedReportEntry)"
                                                single-line
                                                :value="param.action"
                                                @change="updateFormBasedOnAction(param)"
                                              />
                                            </v-col>

                                            <v-col
                                              v-if="param.action === 'filterBy' && (!param.conditions || !param.conditions.length)"
                                              class="mt-2 py-0"
                                            >
                                              <v-autocomplete
                                                v-model="param.propertyName"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                dense
                                                :items="getPropertyNameOptions(selectedReportEntry)"
                                                label="Property"
                                                name="filterBy"
                                                :rules="rulesPropertyName(param)"
                                                single-line
                                                :value="param.propertyName"
                                              />
                                            </v-col>

                                            <v-col
                                              v-if="param.action === 'groupBy'"
                                              class="mt-2 py-0"
                                            >
                                              <v-autocomplete
                                                v-model="param.propertyName"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                dense
                                                :items="getGroupByOptions(selectedReportEntry)"
                                                label="Properties"
                                                multiple
                                                name="groupBy"
                                                :rules="rulesRequired(selectedReportEntry)"
                                                single-line
                                                :value="param.propertyName"
                                              />
                                            </v-col>

                                            <v-col
                                              v-if="param.action === 'mergeBy'"
                                              class="mt-2 py-0"
                                            >
                                              <v-autocomplete
                                                v-model="param.propertyName"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                dense
                                                :items="getMergeByOptions(selectedReportEntry)"
                                                label="Properties"
                                                multiple
                                                name="mergeBy"
                                                :rules="rulesRequired(selectedReportEntry)"
                                                single-line
                                                :value="param.propertyName"
                                              />
                                            </v-col>

                                            <v-col
                                              v-if="param.action === 'sortBy'"
                                              class="mt-2 py-0"
                                            >
                                              <v-autocomplete
                                                v-model="param.propertyName"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                dense
                                                :items="getPropertyNameOptions(selectedReportEntry)"
                                                label="Property"
                                                name="sortBy"
                                                :rules="rulesRequired(selectedReportEntry)"
                                                single-line
                                                :value="param.propertyName"
                                              />
                                            </v-col>

                                            <v-col
                                              v-if="['filterBy', 'groupBy', 'mergeBy'].indexOf(param.action) === -1 || (param.action === 'filterBy' && (!param.conditions || !param.conditions.length))"
                                              class="mt-2 py-0"
                                            >
                                              <v-autocomplete
                                                v-model="param.operator"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                dense
                                                :disabled="param.action !== 'filterBy'"
                                                :items="getOperatorOptions(param, selectedReportEntry)"
                                                label="Operator"
                                                name="operator"
                                                :rules="rulesRequired(selectedReportEntry)"
                                                single-line
                                                :value="param.operator"
                                              />
                                            </v-col>

                                            <v-col
                                              v-if="['filterBy', 'groupBy', 'mergeBy'].indexOf(param.action) === -1 || (param.action === 'filterBy' && (!param.conditions || !param.conditions.length))"
                                              class="mt-2 py-0"
                                            >
                                              <v-combobox
                                                v-model="userInputValueSync[paramIndex]"
                                                autocomplete="off"
                                                class="font-weight-medium"
                                                :clearable="false"
                                                :chips="false"
                                                :deletable-chips="false"
                                                :disabled="['filterBy', 'sortBy'].indexOf(param.action) === -1"
                                                :dense="true"
                                                height="auto"
                                                :hide-no-data="!param.value"
                                                :items="inputOptions[paramIndex]"
                                                :label="getUserInputValueLabel(param)"
                                                :search-input.sync="userInputValuesText[paramIndex]"
                                                :multiple="false"
                                                :required="param.action === 'filterBy'"
                                                :single-line="true"
                                                :value="param.value"
                                                @keyup="setParameterValue(param, paramIndex)"
                                                @change="selectParameterValue($event, param, paramIndex)"
                                              >
                                                <template v-slot:append>
                                                  <v-expand-transition>
                                                    <v-col
                                                      v-if="param.action === 'filterBy' && isColumnTypeDate(param.propertyName)"
                                                      class="col-auto pa-0"
                                                    >
                                                      <v-menu
                                                        :ref="`formDatePicker${paramIndex}`"
                                                        v-model="param.datePicker"
                                                        :close-on-content-click="false"
                                                        :nudge-bottom="32"
                                                        :nudge-left="270"
                                                        origin="top right"
                                                        transition="scale-transition"
                                                        min-width="auto"
                                                      >
                                                        <template v-slot:activator="{ on, attrs }">
                                                          <v-btn
                                                            color="primary"
                                                            dark
                                                            :disabled="param.action !== 'filterBy'"
                                                            icon
                                                            x-small
                                                            v-bind="attrs"
                                                            v-on="on"
                                                          >
                                                            <v-icon>
                                                              calendar_month
                                                            </v-icon>
                                                          </v-btn>
                                                        </template>

                                                        <v-date-picker
                                                          v-model="param.date"
                                                          no-title
                                                          scrollable
                                                          :range="param.operator === 'range'"
                                                        >
                                                          <v-spacer />

                                                          <v-btn
                                                            text
                                                            color="primary"
                                                            @click="cancelParameterDateValue(param)"
                                                          >
                                                            Cancel
                                                          </v-btn>

                                                          <v-btn
                                                            text
                                                            color="primary"
                                                            @click="updateParameterDateValue(param, paramIndex)"
                                                          >
                                                            OK
                                                          </v-btn>
                                                        </v-date-picker>
                                                      </v-menu>
                                                    </v-col>

                                                    <v-col
                                                      v-else
                                                      class="pa-0"
                                                    >
                                                      <v-menu
                                                        v-model="param.dialog"
                                                        :nudge-bottom="32"
                                                        :nudge-left="250"
                                                        transition="scale-transition"
                                                        width="250px"
                                                      >
                                                        <template v-slot:activator="{ on, attrs }">
                                                          <v-btn
                                                            icon
                                                            small
                                                          >
                                                            <v-icon
                                                              small
                                                              outlined
                                                              title="Learn more about inputting values"
                                                              :style="`transition: transform .3s ease-in-out !important;${param.value === 'ASC' ? 'transform: rotate(180deg);' : ''}`"
                                                              v-bind="attrs"
                                                              v-on="on"
                                                            >
                                                              {{ getIconForValueTypes(param) }}
                                                            </v-icon>
                                                          </v-btn>
                                                        </template>

                                                        <v-card width="280px">
                                                          <v-card-text>
                                                            <v-col
                                                              class="caption coolgray--text"
                                                            >
                                                              <v-row>
                                                                <v-col class="pt-0">
                                                                  <span class="font-weight-black">Hint</span>
                                                                </v-col>
                                                              </v-row>

                                                              <v-row
                                                                v-if="param.action === 'sortBy'"
                                                                class="align-center"
                                                              >
                                                                <v-col class="pt-0">
                                                                  <p class="font-weight-medium pr-1">
                                                                    <strong>ASC</strong> sorts values in ascending order, e.g. A-Z and 123.
                                                                  </p>
                                                                  <p class="font-weight-medium pr-1 mb-0">
                                                                    <strong>DSC</strong> sorts values in descending order, e.g. Z-A and 321.
                                                                  </p>
                                                                </v-col>
                                                              </v-row>

                                                              <v-row
                                                                v-else-if="param.action === 'filterBy' && isColumnTypeDate(param.propertyName)"
                                                                class="align-center"
                                                              >
                                                                <v-col class="pt-0">
                                                                  <p class="font-weight-medium pr-1">
                                                                    Dates can be entered in the <span class="font-weight-bold">YYYY-MM-DD</span> format.
                                                                  </p>
                                                                  <p class="font-weight-medium pr-1">
                                                                    Ranges can be entered in the <span class="font-weight-bold">YYYY-MM-DD,YYYY-MM-DD</span> format.
                                                                  </p>
                                                                  <p class="font-weight-medium pr-1 mb-0">
                                                                    Relative dates can be entered using numbers and units, e.g. <span class="font-weight-bold">-1 day</span>.
                                                                    We've also provided some convenience values:
                                                                    <span class="font-weight-bold">yesterday</span>,
                                                                    <span class="font-weight-bold">today</span>,
                                                                    and <span class="font-weight-bold">tomorrow</span>.
                                                                  </p>
                                                                </v-col>
                                                              </v-row>

                                                              <v-row
                                                                v-else-if="currentDefaultColumnsByKey[param.propertyName] && ['number', 'numerical'].indexOf(currentDefaultColumnsByKey[param.propertyName].fieldType) !== -1"
                                                                class="align-center"
                                                              >
                                                                <v-col class="font-weight-medium pt-0">
                                                                  <p class="font-weight-medium pr-1 mb-0">
                                                                    Number values are treated similarly to strings. Comma delimiters are optional.
                                                                  </p>
                                                                </v-col>
                                                              </v-row>

                                                              <v-row
                                                                v-else
                                                                class="align-center"
                                                              >
                                                                <v-col class="font-weight-medium pt-0">
                                                                  <p class="font-weight-medium pr-1 mb-0">
                                                                    Text values are normalized to lowercase strings.
                                                                  </p>
                                                                </v-col>
                                                              </v-row>
                                                            </v-col>
                                                          </v-card-text>
                                                        </v-card>
                                                      </v-menu>
                                                    </v-col>
                                                  </v-expand-transition>
                                                </template>
                                              </v-combobox>
                                            </v-col>

                                            <v-col
                                              v-if="allowEdits"
                                              class="col-auto py-0 pl-2 ml-0"
                                            >
                                              <v-btn
                                                :color="secondaryColor"
                                                icon
                                                small
                                              >
                                                <v-icon
                                                  small
                                                  outlined
                                                  title="Copy Parameter"
                                                  @click.stop="addReportParameter(paramIndex, selectedReportEntry)"
                                                >
                                                  content_copy
                                                </v-icon>
                                              </v-btn>

                                              <v-btn
                                                color="toasted"
                                                icon
                                                small
                                              >
                                                <v-icon
                                                  class="material-icons-outlined"
                                                  :disabled="selectedReportEntry.parameters.length <= 1"
                                                  small
                                                  :title="selectedReportEntry.parameters.length <= 1 ? 'One or more parameters required' : 'Delete Parameter'"
                                                  @click.stop="deleteReportParameter(paramIndex, selectedReportEntry)"
                                                >
                                                  backspace
                                                </v-icon>
                                              </v-btn>
                                            </v-col>
                                          </v-row>

                                          <v-row v-if="param.conditions && param.conditions.length">
                                            <v-col>
                                              <draggable v-model="param.conditions">
                                                <v-row
                                                  v-for="(condition, conditionIndex) in param.conditions"
                                                  :key="conditionIndex"
                                                  @dragstart="dragCondition"
                                                  @dragend="dropCondition(param.conditions)"
                                                >
                                                  <v-col class="pt-0">
                                                    <v-card
                                                      class="pa-2"
                                                      outlined
                                                    >
                                                      <v-card-text class="py-0 pl-1 pr-0">
                                                        <v-row>
                                                          <v-col class="col-auto py-0 pl-0 pr-0 ma-auto">
                                                            <v-icon
                                                              class="ml-2 mr-0 cursor-grab"
                                                              color="coolgray"
                                                              small
                                                              title="Drag to Reorder Parameter"
                                                              :disabled="param.conditions.length <= 1"
                                                            >
                                                              drag_indicator
                                                            </v-icon>
                                                          </v-col>

                                                          <v-col
                                                            class="mt-2 mb-4 py-0"
                                                          >
                                                            <v-autocomplete
                                                              v-model="condition.conjunction"
                                                              autocomplete="off"
                                                              class="font-weight-medium"
                                                              dense
                                                              hide-details
                                                              :disabled="conditionIndex === 0"
                                                              :items="getConjunctionOptionsForCondition(conditionIndex)"
                                                              label="Conjunction"
                                                              name="conditionConjunction"
                                                              :rules="rulesRequired(selectedReportEntry)"
                                                              single-line
                                                              :value="condition.conjunction"
                                                            />
                                                          </v-col>

                                                          <v-col
                                                            class="mt-2 mb-4 py-0"
                                                          >
                                                            <v-autocomplete
                                                              v-model="condition.propertyName"
                                                              autocomplete="off"
                                                              class="font-weight-medium"
                                                              dense
                                                              hide-details
                                                              :items="getPropertyNameOptions(selectedReportEntry)"
                                                              label="Property"
                                                              name="conditionPropertyName"
                                                              :rules="rulesRequired(selectedReportEntry)"
                                                              single-line
                                                              :value="condition.propertyName"
                                                            />
                                                          </v-col>

                                                          <v-col
                                                            class="mt-2 mb-4 py-0"
                                                          >
                                                            <v-autocomplete
                                                              v-model="condition.operator"
                                                              autocomplete="off"
                                                              class="font-weight-medium"
                                                              dense
                                                              hide-details
                                                              :items="getOperatorOptions(condition, selectedReportEntry)"
                                                              label="Operator"
                                                              name="conditionOperator"
                                                              :rules="rulesRequired(selectedReportEntry)"
                                                              single-line
                                                              :value="condition.operator"
                                                            />
                                                          </v-col>

                                                          <v-col
                                                            class="mt-2 mb-4 py-0"
                                                          >
                                                            <v-combobox
                                                              v-model="userInputConditionValueSync[`${paramIndex}/${conditionIndex}`]"
                                                              autocomplete="off"
                                                              class="font-weight-medium"
                                                              :clearable="false"
                                                              :chips="false"
                                                              :deletable-chips="false"
                                                              :dense="true"
                                                              height="auto"
                                                              hide-details
                                                              :hide-no-data="!condition.value"
                                                              :items="inputOptionsForConditions[paramIndex][conditionIndex]"
                                                              label="Value"
                                                              :search-input.sync="userInputConditionValuesText[`${paramIndex}/${conditionIndex}`]"
                                                              :multiple="false"
                                                              :required="true"
                                                              single-line
                                                              :value="condition.value"
                                                              @change="setConditionValue(condition, paramIndex, conditionIndex)"
                                                            >
                                                              <template v-slot:no-data>
                                                                <v-list-item>
                                                                  <v-list-item-content>
                                                                    <v-col class="caption coolgray--text">
                                                                      <v-row class="align-center">
                                                                        <div class="font-weight-medium pr-1">
                                                                          Hint: All non-date values are normalized to lowercase strings at time of comparison.
                                                                        </div>
                                                                      </v-row>
                                                                    </v-col>
                                                                  </v-list-item-content>
                                                                </v-list-item>
                                                              </template>
                                                            </v-combobox>
                                                          </v-col>

                                                          <v-col
                                                            v-if="allowEdits"
                                                            class="col-auto py-0 pl-2 my-auto"
                                                          >
                                                            <v-btn
                                                              :color="secondaryColor"
                                                              icon
                                                              small
                                                            >
                                                              <v-icon
                                                                small
                                                                outlined
                                                                title="Copy Condition"
                                                                @click.stop="addConditionToReportParameter(paramIndex, conditionIndex + 1, selectedReportEntry)"
                                                              >
                                                                content_copy
                                                              </v-icon>
                                                            </v-btn>

                                                            <v-btn
                                                              color="toasted"
                                                              icon
                                                              small
                                                            >
                                                              <v-icon
                                                                class="material-icons-outlined"
                                                                small
                                                                :title="param.conditions.length <= 1 ? 'One or more conditions required' : 'Delete Condition'"
                                                                @click.stop="deleteConditionFromReportParameter(paramIndex, conditionIndex, selectedReportEntry)"
                                                              >
                                                                backspace
                                                              </v-icon>
                                                            </v-btn>
                                                          </v-col>
                                                        </v-row>
                                                      </v-card-text>
                                                    </v-card>
                                                  </v-col>
                                                </v-row>
                                              </draggable>
                                            </v-col>
                                          </v-row>

                                          <v-row
                                            v-if="param.action === 'filterBy'"
                                            class="mt-0"
                                          >
                                            <v-col
                                              :class="`${param.conditions && param.conditions.length ? 'pb-1 pt-1 px-4' : 'pb-1 pt-0 px-4'}`"
                                            >
                                              <v-btn
                                                :color="highlightColor"
                                                text
                                                x-small
                                                @click.stop="addConditionToReportParameter(paramIndex, (param.conditions && param.conditions.length) || 0, selectedReportEntry)"
                                              >
                                                <span :class="`${secondaryColor}--text`">Add Condition</span>
                                              </v-btn>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </v-card>
                                    </v-col>
                                  </v-row>
                                </v-col>
                              </v-row>

                              <v-row
                                class="justify-center pt-4"
                              >
                                <v-divider
                                  class="mx-2 py-2"
                                  vertical
                                />
                              </v-row>

                              <v-row class="justify-center mt-4 mb-0">
                                <v-btn
                                  color="secondary"
                                  class="rounded-pill elevation-0"
                                  outlined
                                  rounded
                                  small
                                  @click.stop="addReportParameter(undefined, selectedReportEntry)"
                                >
                                  <v-icon
                                    :color="secondaryColor"
                                    small
                                    title="Add Parameter"
                                  >
                                    add
                                  </v-icon>
                                  <span :class="`${secondaryColor}--text`">Add Parameter</span>
                                </v-btn>
                              </v-row>
                            </draggable>
                          </v-col>
                        </v-card>
                      </v-form>
                    </v-tab-item>

                    <v-tab-item value="permissions">
                      <v-form
                        :ref="`${formName}ForPermissions`"
                        v-model="isValidFormForPermissions"
                        class="col pa-0"
                      >
                        <v-card
                          class="py-4 elevation-0"
                          max-width="450"
                        >
                          <v-row class="mt-0">
                            <v-col>
                              <v-combobox
                                v-model="selectedReportEntryOwnerId"
                                autocomplete="off"
                                dense
                                :disabled="!selectedReportEntry.canEditOwner"
                                hide-details
                                :items="ownerInputOptions"
                                label="Owner"
                                name="ownerId"
                                outlined
                                :return-object="false"
                                :rules="rulesRequired(selectedReportEntry)"
                                title="This user is responsible for maintaining this report. Only owners can archive or delete a report from all shared users' views."
                              />
                            </v-col>
                          </v-row>

                          <v-row class="mt-0">
                            <v-col>
                              <v-select
                                v-model="selectedReportEntry.scope"
                                autocomplete="off"
                                dense
                                :disabled="!selectedReportEntry.canEditScope"
                                hide-details
                                :items="scopeOptions"
                                label="Read Access"
                                name="scope"
                                outlined
                                :rules="rulesRequired(selectedReportEntry)"
                                title="A global report is available to all users of the current network, while a local report's availability is limited to the owner and their shared users."
                              />
                            </v-col>
                          </v-row>

                          <v-row class="mt-0">
                            <v-col>
                              <v-select
                                v-model="selectedReportEntry.defaultRole"
                                autocomplete="off"
                                dense
                                :disabled="!selectedReportEntry.canEditRole"
                                hide-details
                                :items="roleOptions"
                                label="Write Access"
                                name="defaultRole"
                                outlined
                                :rules="rulesRequired(selectedReportEntry)"
                                title="The general access rights granted to all users of the current network."
                              />
                            </v-col>
                          </v-row>

                          <v-row
                            class="mt-0"
                          >
                            <v-col>
                              <v-btn
                                :color="highlightColor"
                                class="rounded-pill elevation-0"
                                :disabled="!selectedReportEntry.canShareSoft"
                                outlined
                                rounded
                                small
                                @click.stop="toggleModal('Share')"
                              >
                                <v-icon
                                  :color="secondaryColor"
                                  small
                                >
                                  add
                                </v-icon>
                                <span :class="`${secondaryColor}--text`">Share</span>
                              </v-btn>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col class="pt-0 px-4">
                              <v-row class="px-3 pb-1 mt-4">
                                <v-divider />
                              </v-row>

                              <v-row class="mt-0 align-center">
                                <v-col class="col-auto pr-1">
                                  <v-checkbox
                                    v-model="selectedReportEntry.isClientEnforced"
                                    autocomplete="off"
                                    dense
                                    :disabled="!inDevMode"
                                    hide-details
                                    label="Enforce parameters across all reports in network"
                                    name="isClientEnforced"
                                    title="All reports in the network will inherit from this report."
                                  />
                                </v-col>

                                <v-col class="col-auto pl-0 pb-2">
                                  <v-btn
                                    icon
                                    x-small
                                    title="Learn more about enforcing criteria"
                                    @click.stop="toggleModal('EnforceCriteria')"
                                  >
                                    <v-icon
                                      x-small
                                    >
                                      question_mark
                                    </v-icon>
                                  </v-btn>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-card>
                      </v-form>
                    </v-tab-item>

                    <v-tab-item value="settings">
                      <v-form
                        :ref="`${formName}ForSettings`"
                        v-model="isValidFormForSettings"
                        class="col pa-0"
                      >
                        <v-card
                          class="elevation-0"
                          max-width="450"
                        >
                          <v-col>
                            <v-row>
                              <v-col class="px-0">
                                <span class="subtitle-2">
                                  Personal Settings
                                </span>
                              </v-col>
                            </v-row>

                            <v-row
                              class="mt-0 align-center"
                            >
                              <v-col class="col-auto pl-0 pr-1">
                                <v-checkbox
                                  v-model="selectedReportEntryUserRole.isFavorite"
                                  autocomplete="off"
                                  dense
                                  hide-details
                                  label="Display as favorite"
                                  name="isFavorite"
                                  title="Favorite reports appear as quick links atop every report, below metrics."
                                />
                              </v-col>

                              <v-col class="col-auto pl-0 pb-2">
                                <v-btn
                                  icon
                                  x-small
                                  title="Learn more about favorite reports"
                                  @click.stop="toggleModal('DisplayAsFavorite')"
                                >
                                  <v-icon
                                    x-small
                                  >
                                    question_mark
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>

                            <v-row
                              class="mt-0 align-center"
                            >
                              <v-col class="col-auto pl-0 pr-1">
                                <v-checkbox
                                  v-model="selectedReportEntryUserRole.isMetric"
                                  autocomplete="off"
                                  dense
                                  hide-details
                                  label="Display as metric"
                                  name="isMetric"
                                  title="Metrics summarize subsets in other reports. They appear as blue boxes atop every report."
                                />
                              </v-col>

                              <v-col class="col-auto pl-0 pb-2">
                                <v-btn
                                  icon
                                  x-small
                                  title="Learn more about metrics"
                                  @click.stop="toggleModal('DisplayAsMetric')"
                                >
                                  <v-icon
                                    x-small
                                  >
                                    question_mark
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>

                            <v-row
                              class="mt-0 align-center"
                            >
                              <v-col class="col-auto pl-0 pr-1">
                                <v-checkbox
                                  v-model="selectedReportEntryUserRole.isArchived"
                                  autocomplete="off"
                                  dense
                                  hide-details
                                  label="Archive"
                                  name="isArchived"
                                  title="Archived reports are removed from your and shared users' lists of available reports."
                                />
                              </v-col>

                              <v-col class="col-auto pl-0 pb-2">
                                <v-btn
                                  icon
                                  x-small
                                  title="Learn more about archiving reports."
                                  @click.stop="toggleModal('ArchiveReport')"
                                >
                                  <v-icon
                                    x-small
                                  >
                                    question_mark
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-divider class="my-2" />
                            </v-row>

                            <v-row>
                              <v-col class="px-0 pb-0">
                                <span class="subtitle-2">
                                  Global Settings
                                </span>
                              </v-col>
                            </v-row>

                            <v-row class="mt-0">
                              <v-col class="px-0 mt-4">
                                <v-text-field
                                  v-model="selectedReportEntry.reportPath"
                                  autocomplete="off"
                                  dense
                                  :disabled="!selectedReportEntry.canEditReportPath"
                                  hide-details
                                  label="Path"
                                  name="reportPath"
                                  outlined
                                  x-small
                                />
                              </v-col>
                            </v-row>

                            <v-row
                              class="mt-0 align-center"
                            >
                              <v-col class="col-auto pl-0 pr-1">
                                <v-checkbox
                                  v-model="selectedReportEntry.isFavorite"
                                  autocomplete="off"
                                  dense
                                  :disabled="!selectedReportEntry.canEditIsFavorite"
                                  hide-details
                                  label="Display as favorite"
                                  name="isFavorite"
                                  title="Favorite reports appear as quick links atop every report, below metrics."
                                />
                              </v-col>

                              <v-col class="col-auto pl-0 pb-2">
                                <v-btn
                                  icon
                                  x-small
                                  title="Learn more about favorite reports"
                                  @click.stop="toggleModal('DisplayAsFavorite')"
                                >
                                  <v-icon
                                    x-small
                                  >
                                    question_mark
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>

                            <v-row
                              class="mt-0 align-center"
                            >
                              <v-col class="col-auto pl-0 pr-1">
                                <v-checkbox
                                  v-model="selectedReportEntry.isMetric"
                                  autocomplete="off"
                                  dense
                                  :disabled="!selectedReportEntry.canEditIsMetric"
                                  hide-details
                                  label="Display as metric"
                                  name="isMetric"
                                  title="Metrics summarize subsets in other reports. They appear as blue boxes atop every report."
                                />
                              </v-col>

                              <v-col class="col-auto pl-0 pb-2">
                                <v-btn
                                  icon
                                  x-small
                                  title="Learn more about metrics"
                                  @click.stop="toggleModal('DisplayAsMetric')"
                                >
                                  <v-icon
                                    x-small
                                  >
                                    question_mark
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>

                            <v-row
                              class="mt-0 align-center"
                            >
                              <v-col class="col-auto pl-0 pr-1">
                                <v-checkbox
                                  v-model="selectedReportEntry.isArchived"
                                  autocomplete="off"
                                  :color="!selectedReportEntry.canEditIsArchived ? '' : 'toasted'"
                                  :class="!selectedReportEntry.canEditIsArchived ? '' : 'toasted--text'"
                                  dense
                                  :disabled="!selectedReportEntry.canEditIsArchived"
                                  hide-details
                                  label="Archive"
                                  name="isArchived"
                                  title="Archived reports are removed from your and shared users' lists of available reports."
                                />
                              </v-col>

                              <v-col class="col-auto pl-0 pb-2">
                                <v-btn
                                  icon
                                  x-small
                                  title="Learn more about archiving reports."
                                  @click.stop="toggleModal('ArchiveReport')"
                                >
                                  <v-icon
                                    x-small
                                  >
                                    question_mark
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-card>
                      </v-form>
                    </v-tab-item>
                  </v-tabs-items>
                </v-card-text>

                <v-card-actions class="px-0">
                  <v-col>
                    <v-row>
                      <v-divider />
                    </v-row>

                    <v-row class="justify-end py-2">
                      <v-col class="ml-3 mr-2 py-0 d-flex align-center">
                        <v-row class="align-center">
                          <v-col class="col-auto pl-0">
                            <v-btn
                              class="pl-0"
                              :color="secondaryColor"
                              :disabled="!allowEdits"
                              small
                              text
                              @click.stop="cancelForm"
                            >
                              <span class="px-1">
                                Exit
                              </span>
                            </v-btn>
                          </v-col>

                          <v-spacer />

                          <v-col class="col-auto">
                            <v-btn
                              :class="`pa-4 ${allowEdits && (selectedReportEntry.associationId !== 'all-associations' || selectedReportEntry.routeName === 'internal') ? 'rounded-r-0' : 'rounded'} elevation-0`"
                              :color="highlightColor"
                              :disabled="!allowEdits || selectedReportEntry.isTemporary"
                              small
                              @click.stop="resetForm"
                            >
                              <v-icon
                                x-small
                              >
                                undo
                              </v-icon>
                              <span class="pl-1">
                                Undo Edits
                              </span>
                            </v-btn>

                            <v-menu
                              v-if="allowEdits && selectedReportEntry.isProtected && (selectedReportEntry.associationId !== 'all-associations' || selectedReportEntry.routeName === 'internal')"
                              nudge-bottom="4"
                            >
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  class="pa-4 rounded-l-0 elevation-0"
                                  :color="highlightColor"
                                  x-small
                                  style="min-width: 25px !important; width: 25px !important;"
                                  v-on="on"
                                >
                                  <v-icon
                                    x-small
                                    class="material-icons-outlined"
                                  >
                                    expand_more
                                  </v-icon>
                                </v-btn>
                              </template>

                              <v-list>
                                <v-list-item
                                  @click.stop="restoreReportToOriginalSettings"
                                >
                                  <v-list-item-title>
                                    Restore to Template
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </v-col>

                          <v-col class="col-auto">
                            <v-btn
                              :class="`pa-4 ${allowEdits && (selectedReportEntry.associationId !== 'all-associations' || selectedReportEntry.routeName === 'internal') ? 'rounded-r-0' : 'rounded'} elevation-0`"
                              color="primary"
                              :disabled="!allowEdits"
                              small
                              @click.stop="submitForm(false)"
                            >
                              <v-icon
                                small
                                class="material-icons-outlined"
                              >
                                save
                              </v-icon>

                              <span class="pl-1">
                                Save
                              </span>
                            </v-btn>

                            <v-menu
                              v-if="allowEdits && (selectedReportEntry.associationId !== 'all-associations' || selectedReportEntry.routeName === 'internal')"
                              offset-y
                            >
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  class="pa-4 rounded-l-0 elevation-0"
                                  color="primary"
                                  small
                                  style="min-width: 25px !important; width: 25px !important;"
                                  v-on="on"
                                >
                                  <v-icon
                                    small
                                    class="material-icons-outlined"
                                  >
                                    expand_more
                                  </v-icon>
                                </v-btn>
                              </template>

                              <v-list>
                                <v-list-item
                                  :disabled="!allowEdits"
                                  @click.stop="submitForm('view')"
                                >
                                  <v-list-item-title>
                                    Save and View Results
                                  </v-list-item-title>
                                </v-list-item>

                                <!-- <v-list-item-->
                                <!--   :disabled="!allowEdits"-->
                                <!--   @click.stop="submitForm('export')"-->
                                <!-- >-->
                                <!--   <v-list-item-title>-->
                                <!--     Save and Export-->
                                <!--   </v-list-item-title>-->
                                <!-- </v-list-item>-->
                              </v-list>
                            </v-menu>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-divider />
                    </v-row>
                  </v-col>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-card-text>

      <v-dialog
        v-model="showModalDisplayAsFavorite"
        max-width="350"
      >
        <v-card class="pt-4 px-4">
          <v-card-title class="subtitle-2">
            <span>Display as Favorite</span>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col>
                <p>
                  Favorite reports appear as quick links atop every report, below metrics.
                </p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showModalArchiveReport"
        max-width="420"
      >
        <v-card class="pt-4 px-4">
          <v-card-title class="subtitle-2">
            <span>Archive</span>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col>
                <p>
                  Archiving a report exerts the following:
                </p>

                <ul>
                  <li>
                    An <strong>archived</strong> label appears in the report's header.
                  </li>
                  <li class="mt-2">
                    The report no longer appears in your list of available reports.
                  </li>
                  <li class="mt-2">
                    The report remains available to anyone with shared access.
                  </li>
                  <li class="mt-2">
                    The report is accessible to you via the "Show Archived" option, in the top-right
                    corner of the ReportManager.
                  </li>
                </ul>

                <p class="mt-4">
                  To revoke universal access to a report you own, thereby removing it from
                  all users' views, delete it.
                </p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showModalDisplayAsMetric"
        max-width="350"
      >
        <v-card class="pt-4 px-4">
          <v-card-title class="subtitle-2">
            <span>Display as Metric</span>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col>
                <p>
                  Metrics summarize subsets in other reports. They appear as blue boxes atop every report.
                </p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showModalEnforceCriteria"
        max-width="350"
      >
        <v-card class="pt-4 px-4">
          <v-card-title class="subtitle-2">
            <span>Enforce Criteria</span>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col>
                <p>
                  Enforce this report's criteria across all reports.
                  All reports in the network will inherit from this report.
                </p>

                <p>
                  Results excluded by this report are excluded by all reports.
                  Use this feature to remove subsets from the data, such as test line items.
                </p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="confirmCancel"
        max-width="350"
      >
        <v-card class="pa-6">
          <v-card-text>
            <p class="pa-3 mb-0">
              You have unsaved changes.
            </p>
          </v-card-text>

          <v-card-actions>
            <v-btn
              text
              color="coolgray"
              @click.stop="cancelAndCloseForm"
            >
              Exit
            </v-btn>

            <v-btn
              class="font-weight-bold"
              text
              color="primary"
              @click.stop="submitForm"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="confirmDelete"
        max-width="350"
      >
        <v-card class="pa-6">
          <v-card-text class="pb-0">
            <p class="pa-3 mb-0">
              Are you sure you want to delete <span class="font-weight-bold">{{ deleteLabel }}</span>?
            </p>
          </v-card-text>

          <v-card-actions class="justify-center">
            <v-btn
              color="coolgray"
              text
              @click.stop="cancelDeleteReport"
            >
              Cancel
            </v-btn>

            <v-btn
              color="toasted"
              text
              @click.stop="deleteReport"
            >
              Delete it
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="denyDelete"
        max-width="350"
      >
        <v-card class="pa-6">
          <v-card-text>
            <p class="pa-3 mb-0">
              Cannot delete. A default is required.
            </p>
          </v-card-text>

          <v-card-actions>
            <v-btn
              color="primary"
              @click.stop="cancelDeleteReport"
            >
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-form>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import { copyObject, createObject } from 'src/utils/objects'
import { createReport, createReportId, createReportRole } from 'src/report/store/utils/report'

import columns from 'src/utils/columns'
import draggable from 'vuedraggable'
import operators from 'src/utils/operators'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapActions: mapReportActions, mapGetters: mapReportGetters, mapState: mapReportState } = createNamespacedHelpers('report')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'ReportBuilder',
  components: {
    draggable,
  },
  props: {
    allowEdits: {
      type: Boolean,
      default: false,
    },
    formName: {
      type: String,
      default: 'refFormReportManager',
    },
    selectedReportDocId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      confirmCancel: null,
      confirmDelete: null,

      copyLabel: null,
      copiedReport: null,

      deleteIndex: null,
      deleteLabel: null,
      denyDelete: false,

      isDraggingCondition: false,

      draggingParameterIndex: null,
      isDraggingParameter: false,

      isEditingReportLabel: false,
      isResettingForm: false,

      currentOperators: operators.textual,
      isValidFormForParameters: true,
      isValidFormForPermissions: true,
      isValidFormForSettings: true,

      actionOptions: [
        { text: 'Filter', value: 'filterBy' },
        { text: 'Group', value: 'groupBy' },
        // { text: 'Merge', value: 'mergeBy' },
        { text: 'Sort', value: 'sortBy' },
      ],
      scopeOptions: [
        { text: 'Local', value: 'local' },
        { text: 'Global', value: 'global' },
      ],
      sortDirectionOptions: [
        { text: 'ASC', value: 'ASC' },
        { text: 'DSC', value: 'DSC' },
      ],
      sections: [
        {
          text: 'Parameters',
          value: 'parameters',
        },
        {
          text: 'Permissions',
          value: 'permissions',
        },
        {
          text: 'Settings',
          value: 'settings',
        },
      ],

      showBaseReportNameField: false,

      defaultValueMap: {
        filterBy: null,
        groupBy: [],
        mergeBy: [],
        sortBy: null,
      },

      temporaryAction: null,

      userInputDatePickerValues: [],

      userInputValues: [],
      userInputValuesText: [],

      userInputConditionValues: {},
      userInputConditionValuesText: {},
    }
  },
  computed: {
    ...mapAuthGetters([
      'inDevMode',
      'isInternalUser',
      'selfEmail',
      'userEmail',
      'userName',
    ]),
    ...mapLayoutGetters(['iceColor', 'midColor']),
    ...mapReportGetters([
      'hasOwnerRights',
      'hasEditorRights',
    ]),
    ...mapReportState({
      $backupReport: (state) => state.backupReport,
      $roleOptions: (state) => state.roleOptions,
      $selectedReport: (state) => state.selectedReport,
      $showModalArchiveReport: (state) => state.showModalArchiveReport,
      $showModalDisplayAsFavorite: (state) => state.showModalDisplayAsFavorite,
      $showModalDisplayAsMetric: (state) => state.showModalDisplayAsMetric,
      $showModalEnforceCriteria: (state) => state.showModalEnforceCriteria,
    }),
    ...mapToolsGetters([
      'currentDefaultColumns',
      'currentDefaultColumnsByKey',
      'currentGroups',
      'currentNav',
      'currentReportKeyItems',
      'uniqueReportOptions',
      'reportOptionsForSelectedTeam',
      'selectedReportTeam',
    ]),
    ...mapToolsState({
      booleanOptionsAlt: (state) => state.booleanOptionsAlt,
      columnDefaults: (state) => state.columnDefaults,
      groupingOptions: (state) => state.groupingOptions,
      mergingOptions: (state) => state.mergingOptions,
      reportPath: (state) => state.reportPath,
      reportManager: (state) => state.reportManager,
      baseReportName: (state) => state.baseReportName,
    }),
    highlightColor() {
      return this.iceColor
    },
    secondaryColor() {
      return this.midColor
    },

    roleOptions() {
      return this.$roleOptions
    },
    showModalArchiveReport: {
      get() {
        return this.$showModalArchiveReport
      },
      set(value) {
        this.$store.commit('report/showModalArchiveReport', value)
      },
    },
    showModalDisplayAsFavorite: {
      get() {
        return this.$showModalDisplayAsFavorite
      },
      set(value) {
        this.$store.commit('report/showModalDisplayAsFavorite', value)
      },
    },
    showModalDisplayAsMetric: {
      get() {
        return this.$showModalDisplayAsMetric
      },
      set(value) {
        this.$store.commit('report/showModalDisplayAsMetric', value)
      },
    },
    showModalEnforceCriteria: {
      get() {
        return this.$showModalEnforceCriteria
      },
      set(value) {
        this.$store.commit('report/showModalEnforceCriteria', value)
      },
    },

    selectedOrganization() {
      return this.selectedReportTeam
    },
    reportOptions: {
      get() {
        return this.uniqueReportOptions
      },
      set(value) {
        this.$store.commit('tools/temporaryReportOptions', value)
      },
    },
    backupReport: {
      get() {
        return this.$backupReport
      },
      set(value) {
        this.$store.commit('report/backupReport', value)
      },
    },
    selectedReportEntry: {
      get() {
        return this.$selectedReport
      },
      set(newValue) {
        // this.backupReport = (newValue && copyObject(newValue)) || null

        this.$store.commit('report/selectedReport', newValue)

        if (!this.isResettingForm) {
          const { params, query } = this.$route
          const { billingCode, teamId } = this.selectedOrganization || {}
          const { reportPath } = newValue || {}

          const { temporaryAction } = this
          const newAction = temporaryAction || this.$route.query.action

          this.$router.push({
            params,
            query: { ...query, action: newAction, associationId: billingCode, reportPath, subId: teamId },
          })
        } else {
          this.isResettingForm = false
        }

        this.temporaryAction = null
      },
    },
    selectedReportEntryUserRole() {
      const { selectedReportEntry, userEmail } = this
      const { roles } = selectedReportEntry || {}
      return (roles && roles.find((role) => role.userId === userEmail)) || {}
    },
    selectedReportEntryOwnerId: {
      get() {
        const { ownerInputOptions, selectedReportEntry } = this
        const { ownerId } = selectedReportEntry || {}

        return (ownerId && ownerInputOptions.find((option) => option.value === ownerId)) || null
      },
      set(value) {
        const { selectedReportEntry } = this

        selectedReportEntry.ownerId = value

        this.selectedReportEntry = selectedReportEntry
      },
    },
    selectedReportEntryParameters() {
      const { selectedReportEntry } = this
      const { parameters } = selectedReportEntry || {}

      return (parameters || []).map((param) => {
        if (Array.isArray(param.value)) {
          param.value = param.value.join(', ')
        }
        return param
      })
    },
    inputOptions() {
      const { selectedReportEntry } = this
      const { parameters } = selectedReportEntry || {}

      return parameters.map((parameter) => this.getValueOptions(parameter))
    },
    inputOptionsForConditions() {
      const { selectedReportEntry } = this
      const { parameters } = selectedReportEntry || {}

      return parameters.map((parameter) => (!parameter.conditions ? [] : parameter.conditions.map((condition) => this.getValueOptions(condition))))
    },
    rulesRequired() {
      return (query) => {
        const { label, baseReportName } = query
        return [
          (value) => {
            const isStillEditing = !label && !baseReportName
            const isValidValue = isStillEditing || !!value || [0, false].indexOf(value) !== -1
            return isValidValue || 'Required'
          },
        ]
      }
    },
    rulesPropertyName() {
      return (param) => {
        const { action, conditions, operator, propertyName, value } = param
        return [
          () => {
            const isStillEditing = !action || (!operator && !value)
            const hasConditions = conditions && conditions.length
            const isValidValue = isStillEditing || hasConditions || !!propertyName
            return isValidValue || 'Required'
          },
        ]
      }
    },
    rulesReportName() {
      const characterLimit = 75
      const allowedPunctuation = () => '! " # $ % & \' ( ) * + , - . / : ? _ |'
      const hasOtherFieldsPending = () => !this.selectedReportEntry.baseReportName || !this.selectedReportEntry.reportPath
      const isInputDefinedOrPending = (value) => !!value || hasOtherFieldsPending()
      const isValidQueryName = (value) => !value || this.isValidQueryName(value)
      const isValidCharacterCount = (value) => !value || value.length <= characterLimit

      return [
        (value) => isInputDefinedOrPending(value) || 'Required',
        (value) => isValidQueryName(value) || `Allowed characters include the following: ${allowedPunctuation()}`,
        (value) => isValidCharacterCount(value) || `The query name must be ≤ ${characterLimit} allowed characters.`,
      ]
    },
    rulesReportPath() {
      const characterLimit = 75
      const hasOtherFieldsPending = (value) => !value && (!this.selectedReportEntry.baseReportName || !this.selectedReportEntry.reportLabel)
      const isInputDefinedOrPending = (value) => !!value || hasOtherFieldsPending()
      const isDashedLowercaseAlphaNumeric = (value) => !value || this.isDashedLowercaseAlphaNumeric(value)
      const isValidCharacterCount = (value) => !value || value.length <= characterLimit

      return [
        (value) => isInputDefinedOrPending(value) || 'Required',
        (value) => isDashedLowercaseAlphaNumeric(value) || `The query path must be a dash-delimited, lowercase, alphanumeric string with a length under ${characterLimit} characters.`,
        (value) => isDashedLowercaseAlphaNumeric(value) || `The query path must be a dash-delimited, lowercase, alphanumeric string with a length under ${characterLimit} characters.`,
        (value) => isValidCharacterCount(value) || `Too long. Length must be under ${characterLimit} characters.`,
      ]
    },

    currentSection: {
      get() {
        return this.$route.query.section
      },
      set(value) {
        const { params, query } = this.$route
        this.$router.replace({ params, query: { ...query, section: value } })
      },
    },

    ownerInputOptions() {
      const { selectedReportEntry, selfEmail, userEmail } = this
      const allUserOption = { text: 'iAdOps', value: 'all-users' }
      const currentUserOption = { text: userEmail, value: userEmail }
      const selectedReportEntryOwnerId = selectedReportEntry && selectedReportEntry.ownerId
      const isMissingSelectionAmongOptions = selectedReportEntryOwnerId && ['all-users', userEmail].indexOf(selectedReportEntryOwnerId) === -1
      const options = [allUserOption, currentUserOption]

      if (selfEmail && selfEmail !== userEmail) {
        options.push({ text: selfEmail, value: selfEmail })
      }

      if (isMissingSelectionAmongOptions) {
        options.push({ text: selectedReportEntryOwnerId, value: selectedReportEntryOwnerId })
      }

      return options
    },

    userInputValueSync: {
      get() {
        return this.userInputValues
      },
      set(value) {
        this.userInputValues = value
      },
    },

    userInputConditionValueSync: {
      get() {
        return this.userInputConditionValues
      },
      set(value) {
        this.userInputConditionValues = value
      },
    },
  },
  watch: {
    $backupReport: {
      deep: true,
      handler() {},
    },
    $selectedReport: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.isResettingForm = true
        this.updateForm(newValue)
        this.isResettingForm = false
      },
    },
    userInputValues: {
      deep: true,
      handler() {},
    },
    userInputValueSync: {
      deep: true,
      handler() {},
    },
    userInputValuesText: {
      deep: true,
      handler() {},
    },
    userInputConditionValues: {
      deep: true,
      handler() {},
    },
    userInputConditionValuesText: {
      deep: true,
      handler() {},
    },
  },
  methods: {
    ...mapReportActions(['updateReport']),

    getUserInputValueLabel(param) {
      const useDateRange = () => param.operator === 'range' && 'Date Range'
      const useRelativeDate = () => this.isColumnTypeDate(param.propertyName) && 'Date'
      return useDateRange() || useRelativeDate() || 'Value'
    },

    toggleModal(modalName, modalData) {
      const storeName = 'report'
      this.$store.dispatch('tools/toggleModal', { modalData, modalName, storeName })
    },

    validate() {
      return true
    },
    reset() {
      return true
    },
    resetValidation() {
      return true
    },

    dragParameter(index) {
      if (!this.isDraggingCondition) {
        this.draggingParameterIndex = index
        this.isDraggingParameter = true
      }
    },
    dropParameter() {
      this.isDraggingParameter = false
      this.draggingParameterIndex = null
    },

    dragCondition() {
      this.isDraggingCondition = true
    },
    dropCondition(conditions) {
      const updateAllConjunctions = () => {
        const getValidConjunction = (conjunction) => (conjunction === 'IF' ? 'AND' : conjunction)

        conditions.forEach((condition, conditionIndex) => {
          const setFirstCondition = () => conditionIndex === 0 && 'IF'
          condition.conjunction = setFirstCondition() || getValidConjunction(condition.conjunction)
        })
      }
      const resetState = () => {
        this.isDraggingCondition = false
        this.draggingConditionIndex = null
      }

      updateAllConjunctions()
      resetState()
    },

    getConjunctionOptionsForCondition(conditionIndex) {
      const optionIf = { disabled: conditionIndex !== 0, text: 'IF', value: 'IF' }
      const optionAnd = { disabled: conditionIndex === 0, text: 'AND', value: 'AND' }
      const optionOr = { disabled: conditionIndex === 0, text: 'OR', value: 'OR' }

      return [optionIf, optionAnd, optionOr]
    },

    restoreReportToOriginalSettings() {
      this.$store.dispatch('report/restoreReport', this.selectedReportEntry)
    },

    updateForm(report) {
      const userInputValuesText = []
      const userInputValues = []

      report.parameters.forEach((param, paramIndex) => {
        const { value } = this.getParameterValueAsOption(param, paramIndex)
        const flatten = () => {
          const flattenArray = Array.isArray(param.value) && param.value.length > 1
          return flattenArray ? param.value.join(', ') : param.value
        }
        const paramValueAsText = typeof param.value === 'string' ? param.value : flatten()

        userInputValuesText.push(paramValueAsText)
        userInputValues.push(value)

        if (param.conditions) {
          param.conditions.forEach((condition, conditionIndex) => {
            const { value: conditionValue } = this.getConditionValueAsOption(condition, paramIndex, conditionIndex)
            this.userInputConditionValuesText[`${paramIndex}/${conditionIndex}`] = condition.value
            this.userInputConditionValues[`${paramIndex}/${conditionIndex}`] = conditionValue
          })
        }
      })

      this.userInputValuesText = userInputValuesText
      this.userInputValues = userInputValues
    },

    copyReport() {
      const { copiedReport, selectedReportEntry } = this

      const oldReport = copyObject(selectedReportEntry)
      const newReport = copyObject(copiedReport)

      const newReports = this.entry.reduce((newReportsArray, report, reportIndex) => {
        if (reportIndex === oldReport.index) {
          newReportsArray.push(oldReport)
        } else {
          newReportsArray.push(copyObject(report))
        }
        return newReportsArray
      }, [])

      newReports.push(newReport)

      return { newReport, newReports }
    },
    copyReportToSelectedOrg(selectedReport) {
      const { currentNav, inDevMode, reportOptionsForSelectedTeam, selectedOrganization, userEmail } = this
      const isAllAssociation = selectedOrganization && selectedOrganization.billingCode === 'all-associations' && selectedOrganization.teamId === 'all-secondary'
      const scope = isAllAssociation && inDevMode ? 'global' : 'local'
      const ownerId = isAllAssociation && inDevMode ? 'all-users' : userEmail
      const docId = createReportId()
      const newRoleProperties = {
        role: 'owner',
      }
      const newRole = createReportRole(newRoleProperties, userEmail)

      const newReport = createReport(currentNav, {
        associationId: selectedReport.associationId || selectedOrganization.billingCode,
        baseReportName: currentNav.baseReportName,
        columns: selectedReport.columns,
        defaultRole: selectedReport.defaultRole || 'viewer',
        docId,
        index: reportOptionsForSelectedTeam.length,
        isClientEnforced: inDevMode ? selectedReport.isClientEnforced : false,
        isDeleted: false,
        isEditable: true,
        isMetric: selectedReport.isMetric || false,
        parameters: selectedReport.parameters,
        reportLabel: `${selectedReport.reportLabel} [Copy]`,
        reportPath: docId,
        roles: [newRole],
        routeName: selectedReport.routeName || currentNav.routeName,
        scope,
        shares: [],
        subId: selectedReport.subId || selectedOrganization.teamId,
        ownerId,
      })

      newReport.isTemporary = true

      this.temporaryAction = 'create'

      this.$store.commit('report/isCreatingReport', true)

      this.reportOptions = [newReport]
      this.selectedReportEntry = newReport
    },

    cancelDeleteReport() {
      this.denyDelete = false
      this.confirmDelete = false
      this.deleteIndex = null
      this.deleteLabel = null
    },
    confirmDeleteReport() {
      const { inDevMode, selectedReportEntry, userEmail } = this
      const isCurrentUserTheReportOwner = () => selectedReportEntry.ownerId === userEmail
      const denyRightsToEdit = () => {
        const { isClientEnforced } = selectedReportEntry
        const rights = isCurrentUserTheReportOwner || inDevMode
        return isClientEnforced && !rights
      }

      if (denyRightsToEdit()) {
        this.denyDelete = true
      } else {
        this.deleteIndex = selectedReportEntry.index
        this.deleteLabel = selectedReportEntry.reportLabel
        this.confirmDelete = true
      }
    },
    deleteReport() {
      if (this.selectedReportEntry.isTemporary) {
        this.selectedReportEntry = null
        this.reportOptions = []
      } else {
        this.updateReport({ ...this.selectedReportEntry, isDeleted: true })
      }
      this.cancelDeleteReport()
    },

    cancelParameterDateValue(param) {
      param.datePicker = false
    },
    updateParameterDateValue(param, paramIndex) {
      param.value = param.date
      this.userInputValuesText[paramIndex] = Array.isArray(param.date) ? param.date.join(', ') : param.date
      this.cancelParameterDateValue(param)
    },

    setParameterValue(param, paramIndex) {
      const inputValue = this.userInputValuesText[paramIndex]
      const getCustomOptionValue = () => typeof inputValue === 'string' && inputValue
      const getSelectedOptionValue = () => (inputValue && typeof inputValue === 'object' && inputValue.value) || inputValue

      param.value = getCustomOptionValue() || getSelectedOptionValue()
    },
    selectParameterValue($event, param, paramIndex) {
      const { value = null } = $event || {}
      this.userInputValuesText[paramIndex] = Array.isArray(value) ? value.join(', ') : value
      this.setParameterValue(param, paramIndex)
    },
    getParameterValueAsOption(parameter, paramIndex) {
      const { isResettingForm, userInputValuesText } = this
      const currentInputText = userInputValuesText[paramIndex]
      const { value: storedInputValue } = parameter
      const currentInputValue = isResettingForm ? storedInputValue : currentInputText || storedInputValue
      const makeCustomOption = () => ({ text: currentInputValue, value: currentInputValue })
      const findOption = () => this.inputOptions[paramIndex] && this.inputOptions[paramIndex].find((option) => option.value === currentInputValue)

      const newValueOption = findOption() || makeCustomOption()

      parameter.value = newValueOption.value

      return newValueOption
    },
    addReportParameter(parameterIndex, selectedReportEntry) {
      const originalParameters = parameterIndex !== undefined && selectedReportEntry.parameters[parameterIndex]

      this.isResettingForm = true

      if (originalParameters) {
        const { action, conditions, propertyName, operator, value } = originalParameters

        selectedReportEntry.parameters.splice(parameterIndex + 1, 0, {
          action: action || 'filterBy',
          conditions: conditions || [],
          conjunction: 'AND',
          propertyName: propertyName || null,
          operator: operator || 'eq',
          value: typeof value === 'undefined' ? null : value,
        })
      } else {
        selectedReportEntry.parameters.push({
          action: 'filterBy',
          conditions: [],
          conjunction: 'AND',
          propertyName: null,
          operator: null,
          value: null,
        })
      }

      this.selectedReportEntry = selectedReportEntry
    },
    deleteReportParameter(paramIndex, selectedReportEntry) {
      this.isResettingForm = true
      selectedReportEntry.parameters.splice(paramIndex, 1)
      this.selectedReportEntry = selectedReportEntry
    },
    addConditionToReportParameter(parameterIndex, conditionIndex, selectedReportEntry) {
      const originalParameters = parameterIndex !== undefined && selectedReportEntry.parameters[parameterIndex]

      this.isResettingForm = true

      if (originalParameters) {
        const { action, conditions, operator, propertyName, value } = originalParameters
        const isExtendedParameter = action === 'filterBy' && Array.isArray(conditions)
        const newCondition = {
          conjunction: 'AND',
          propertyName: propertyName || null,
          operator: operator || null,
          value: value || null,
        }

        if (isExtendedParameter) {
          conditions.splice(conditionIndex, 0, newCondition)

          selectedReportEntry.parameters.splice(parameterIndex, 1, {
            action,
            conditions,
            conjunction: 'AND',
            propertyName: null,
            operator: 'eq',
            value: null,
          })
        } else {
          const currentCondition = {
            conjunction: 'AND',
            propertyName: propertyName || null,
            operator: operator || null,
            value: value || null,
          }

          selectedReportEntry.parameters.splice(parameterIndex, 1, {
            action,
            conditions: [
              currentCondition,
              newCondition,
            ],
            conjunction: 'AND',
            propertyName: null,
            operator: 'eq',
            value: null,
          })
        }
      }

      this.selectedReportEntry = selectedReportEntry
    },
    deleteConditionFromReportParameter(parameterIndex, conditionIndex, selectedReportEntry) {
      const originalParameters = parameterIndex !== undefined && selectedReportEntry.parameters[parameterIndex]

      this.isResettingForm = true

      if (originalParameters) {
        selectedReportEntry.parameters[parameterIndex].conditions.splice(conditionIndex, 1)
      }

      this.selectedReportEntry = selectedReportEntry
    },
    updateReportParameterConjunction(parameterIndex, selectedReportEntry) {
      const originalParameters = parameterIndex !== undefined && selectedReportEntry.parameters[parameterIndex]

      if (originalParameters) {
        const { conjunction } = originalParameters

        selectedReportEntry.parameters[parameterIndex].conjunction = conjunction === 'AND' ? 'OR' : 'AND'
      }

      this.selectedReportEntry = selectedReportEntry
    },

    setConditionValue(condition, paramIndex, conditionIndex) {
      const inputValue = this.userInputConditionValueSync[`${paramIndex}/${conditionIndex}`]
      const getCustomOptionValue = () => typeof inputValue === 'string' && inputValue
      const getSelectedOptionValue = () => inputValue && typeof inputValue === 'object' && inputValue.value

      condition.value = getCustomOptionValue() || getSelectedOptionValue() || inputValue
    },
    getConditionValueAsOption(condition, paramIndex, conditionIndex) {
      const { isResettingForm, userInputConditionValuesText } = this
      const currentInputText = userInputConditionValuesText[`${paramIndex}/${conditionIndex}`]
      const { value: storedInputValue } = condition
      const currentInputValue = isResettingForm ? storedInputValue : currentInputText || storedInputValue
      const makeCustomOption = () => ({ text: currentInputValue, value: currentInputValue })
      const findOption = () => this.inputOptionsForConditions[paramIndex][conditionIndex].find((option) => option.value === currentInputValue)

      const newValueOption = findOption() || makeCustomOption()

      condition.value = newValueOption.value

      return newValueOption
    },

    export() {
      const { params, query } = this.$route

      this.$router.push({
        params,
        query: { ...query, action: 'export' },
      })
    },
    close() {
      const { params, query } = this.$route

      this.$router.push({
        params,
        query: { ...query, action: 'read' },
      })
    },
    emptyForm() {
      this.selectedReportEntry = null
    },
    cancelForm() {
      this.isResettingForm = true
      this.emptyForm()
    },
    cancelAndCloseForm() {
      this.cancelForm()
      this.close()
    },
    errorForm(message) {
      const status = 'error'

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', { message, status }, { root: true })

      return Promise.resolve(true)
    },
    resetForm() {
      this.isResettingForm = true
      this.selectedReportEntry = copyObject(this.backupReport)
    },
    submitForm(action) {
      const { selectedReportEntry } = this
      const isReportDefined = !!selectedReportEntry

      this.$store.commit('report/isSubmittingReportUpdate', true)

      const applyReport = async () => {
        if (selectedReportEntry.associationId !== 'all-associations' || selectedReportEntry.routeName === 'internal') {
          const endpointKey = selectedReportEntry.baseReportName || selectedReportEntry.routeName
          const params = {
            baseReportName: selectedReportEntry.baseReportName,
            routeName: selectedReportEntry.routeName,
          }
          const query = {
            associationId: selectedReportEntry.associationId,
            reportPath: selectedReportEntry.reportPath,
            subId: selectedReportEntry.subId,
          }

          return this.$store.dispatch('tools/requestAndRender', { endpointKey, params, query }, { root: true })
        }

        return true
      }
      const hydrateView = async () => {
        this.$store.commit('report/isSubmittingReportUpdate', false)

        if (action === 'view') {
          this.temporaryAction = 'read'
        } else if (action === 'export') {
          this.export()
        }

        applyReport()

        this.selectedReportEntry = createObject(this.selectedReportEntry, { isTemporary: false })

        return Promise.resolve(true)
      }

      const error = () => this.errorForm('Please fill required fields correctly.')
      const success = () => {
        if (isReportDefined) {
          this.updateReport(this.selectedReportEntry).then(hydrateView)
        } else {
          this.errorForm(isReportDefined ? 'Save Error' : 'Permission Denied')
        }
      }

      return this.validate() ? success() : error()
    },

    editReportLabelForm() {
      this.isEditingReportLabel = true
    },
    saveReportLabelForm() {
      this.isEditingReportLabel = false
    },

    /**
     * Validates alphanumeric strings using allowed punctuation.
     * Allowed punctuation: !"#$%&'()*+,-./:?_| and space
     * @param str
     * @returns {boolean}
     */
    isValidQueryName(str) {
      // @TODO Refactor isValidQueryName for perf if TextEncoder().encode() is faster than split and charCodeAt.
      const invalid = str && str.split('').find((character, characterIndex) => {
        const code = str.charCodeAt(characterIndex)
        const isNumeric = () => code > 47 && code < 58
        const isLowercaseLetter = () => code > 96 && code < 123
        const isUppercaseLetter = () => code > 64 && code < 91
        const isValidPunctuation = () => (code > 31 && code < 48) || [58, 63, 95, 124].indexOf(code) !== -1
        return !(isValidPunctuation() || isNumeric() || isLowercaseLetter() || isUppercaseLetter())
      })

      return !invalid
    },
    isDashedLowercaseAlphaNumeric(str) {
      const invalid = str && str.split('').find((character, characterIndex) => {
        const code = str.charCodeAt(characterIndex)

        const isDash = () => code === 45
        const isNumeric = () => code > 47 && code < 58
        const isLowercaseLetter = () => code > 96 && code < 123

        return !isDash() && !isNumeric() && !isLowercaseLetter()
      })

      return !invalid
    },

    isColumnTypeDate(propertyName) {
      return columns.isTypeDate(propertyName)
    },

    getActionIcon(action) {
      const actionIconMap = {
        columnFreeze: 'pin',
        columnReorder: 'drag',
        columnToggle: 'check_box',
        filterBy: 'filter_list',
        sortBy: 'sort_by_alpha',
        groupBy: 'grain',
        mergeBy: 'flip_to_front',
      }

      return actionIconMap[action] || 'circle'
    },
    getIconForValueTypes(param) {
      const { action, propertyName } = param

      const { currentDefaultColumnsByKey } = this
      const selectedColumn = (currentDefaultColumnsByKey && currentDefaultColumnsByKey[propertyName]) || {}
      const { fieldType = selectedColumn.formatAs } = selectedColumn

      const iconDate = 'calendar_month'
      const iconNumber = '1x_mobiledata'
      const iconText = 'format_size'
      const showSortIcon = () => action === 'sortBy' && 'sort'

      const valueTypeActionIconMap = {
        boolean: iconText,
        currency: iconNumber,
        date: iconDate,
        defaults: 'circle',
        hhmm: iconNumber,
        indicator: iconNumber,
        microCurrency: iconNumber,
        number: iconNumber,
        numerical: iconNumber,
        strict: iconText,
        text: iconText,
        textual: iconText,
      }

      return showSortIcon() || (typeof fieldType === 'string' && valueTypeActionIconMap[fieldType]) || valueTypeActionIconMap.text
    },

    updateFormBasedOnAction(param) {
      const { action, operator } = param

      const actionsWithoutOperators = ['groupBy', 'sortBy']
      const isActionRequiringOperatorManipulation = actionsWithoutOperators.indexOf(action) !== -1

      if (isActionRequiringOperatorManipulation && operator !== 'eq') {
        param.operator = 'eq'
      }

      param.value = this.defaultValueMap[action] || null
    },

    getGroupByOptions(query) {
      // Get all the group options, then find the ones that match this baseReportName.
      return (query.baseReportName && this.groupingOptions[query.baseReportName]) || []
    },
    getMergeByOptions(query) {
      // Get all the merge options, then find the ones that match this baseReportName.
      return (query.baseReportName && this.mergingOptions[query.baseReportName]) || []
    },
    getPropertyNameOptions() {
      const { currentDefaultColumns } = this

      return (currentDefaultColumns && currentDefaultColumns.map((column) => ({
        text: column.columnName,
        value: column.key,
      }))) || []
    },
    getOperatorOptions(param) {
      // Get all the operators, then find the ones that match the type of the parameter.
      const { currentDefaultColumnsByKey } = this
      const selectedColumn = currentDefaultColumnsByKey && currentDefaultColumnsByKey[param.propertyName]
      const { fieldType } = selectedColumn || {}

      const inputFormat = {
        boolean: ['boolean'],
        date: ['date'],
        number: ['numerical'],
        numerical: ['numerical'],
        strict: ['strict'],
        text: ['strict', 'textual'],
      }
      const makeDefaultOptions = () => [
        { header: 'Text' },
        ...operators.strict,
        ...operators.textual,
        { header: 'Boolean' },
        ...operators.boolean,
        { header: 'Number or Date' },
        ...operators.numerical,
        { header: 'Date' },
        ...operators.date,
      ]
      const foundInputFormats = inputFormat[fieldType]
      const getOperatorOptions = () => foundInputFormats.reduce((operatorOptions, optionType) => {
        operatorOptions.push(...operators[optionType])
        return operatorOptions
      }, [])

      return (foundInputFormats && getOperatorOptions()) || makeDefaultOptions()
    },
    getValueOptions(param) {
      const { action = 'filterBy' } = param

      const actionMap = {
        columnFreeze: () => {},
        columnReorder: () => {},
        columnToggle: () => {},
        filterBy: () => {
          const { operator, propertyName } = param

          const { currentDefaultColumnsByKey } = this
          const selectedColumn = currentDefaultColumnsByKey && currentDefaultColumnsByKey[propertyName]
          const { fieldType } = selectedColumn || {}

          const relativeDateOptions = [
            // { header: 'Relative Units' },
            { text: '3 Days Ago', value: '-3 days' },
            { text: '3 Days from Today', value: '+3 days' },
            { divider: true },
            // { header: 'Custom Date' },
            { text: 'Yesterday', value: 'yesterday' },
            { text: 'Today', value: 'today' },
            { text: 'Tomorrow', value: 'tomorrow' },
          ]
          const relativeDateRangeOptions = [
            { text: 'Last 7 Days', value: '-1 week, today' },
            { text: 'Next 7 Days', value: 'today, +1 week' },
            { divider: true },
            { text: 'This Week', value: 'this-week' },
            { text: 'This Month', value: 'this-month' },
            { text: 'This Year', value: 'this-year' },
            { divider: true },
            { text: 'Last Week', value: 'last-week' },
            { text: 'Last Month', value: 'last-month' },
            { text: 'Last Year', value: 'last-year' },
            { divider: true },
            { text: 'Week-to-Date', value: 'week-to-date' },
            { text: 'Month-to-Date', value: 'month-to-date' },
            { text: 'Year-to-Date', value: 'year-to-date' },
          ]
          const getDateParameters = () => {
            const isColumnTypeDate = fieldType === 'date' && relativeDateOptions
            return isColumnTypeDate && (operator === 'range' ? relativeDateRangeOptions : relativeDateOptions)
          }

          const propertyMap = {
            boolean: () => this.booleanOptionsAlt,
          }
          const { currentReportKeyItems } = this
          const getPropertyValuesFromData = () => {
            const reduceUniqueValueOptions = (uniqueValueOptions, item) => {
              const foundValue = item[propertyName]
              const getArrayValue = () => Array.isArray(foundValue) && foundValue.join(', ')
              const getObjectValue = () => typeof foundValue === 'object' && foundValue && JSON.stringify(foundValue)
              const value = getArrayValue() || getObjectValue() || foundValue
              const isUniqueValue = uniqueValueOptions.indexOf(value) === -1

              if (isUniqueValue) {
                uniqueValueOptions.push({
                  text: value,
                  value,
                })
              }

              return uniqueValueOptions
            }
            const { associationId, reportPath, subId } = this.$route.query
            const currentReport = currentReportKeyItems && currentReportKeyItems[associationId] && currentReportKeyItems[associationId][subId] && currentReportKeyItems[associationId][subId][reportPath]
            const { items } = currentReport || {}

            return (Array.isArray(items) && items.reduce(reduceUniqueValueOptions, [])) || []
          }
          const getPropertyValues = () => (typeof fieldType === 'string' && propertyMap[fieldType] ? propertyMap[fieldType]() : getPropertyValuesFromData())
          const getParameters = () => getPropertyValues() || []

          return getDateParameters() || getParameters()
        },
        groupBy: () => [],
        mergeBy: () => [],
        sortBy: () => this.sortDirectionOptions,
      }

      return typeof actionMap[action] === 'function' && actionMap[action]()
    },
  },
}
</script>
